import React, { useState, useEffect, useCallback } from "react";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiX,
  FiLoader,
  FiInbox,
  FiCheckCircle,
  FiZap,
  FiClock,
} from "react-icons/fi";
import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { ToastContainer } from "react-toastify";

const SubscriptionManagement = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Form State matching your Mongoose Schema
  const [formData, setFormData] = useState({
    planType: "monthly",
    title: "",
    validityDays: "",
    price: "",
    originalPrice: "",
    discountPercent: "",
    features: [""],
  });

  // --- API: Fetch Listing ---
  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await makeRequestAuth(
        "post",
        "/admin/getSubscriptionPlans",
        {},
      );
      if (res?.code === 0) {
        setPlans(res.data || []);
      } else {
        showErrorToast(res?.message || "Failed to load plans");
      }
    } catch (error) {
      showErrorToast("Server error while fetching plans");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // --- API: Add / Update ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanFeatures = formData.features.filter((f) => f.trim() !== "");
    const payload = { ...formData, features: cleanFeatures };

    try {
      let res;
      if (editingId) {
        res = await makeRequestAuth("post", "/admin/updateSubscriptionPlan", {
          ...payload,
          planId: editingId,
        });
      } else {
        res = await makeRequestAuth(
          "post",
          "/admin/addSubscriptionPlan",
          payload,
        );
      }

      if (res?.code === 0) {
        showSuccessToast(editingId ? "Plan updated" : "Plan added");
        closeModal();
        fetchPlans();
      } else {
        showErrorToast(res?.message || "Operation failed");
      }
    } catch (error) {
      showErrorToast("Something went wrong");
    }
  };

  // --- API: Delete ---
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this subscription plan?")) return;
    try {
      const res = await makeRequestAuth(
        "post",
        "/admin/deleteSubscriptionPlan",
        { planId: id },
      );
      if (res?.code === 0) {
        showSuccessToast("Plan deleted");
        fetchPlans();
      }
    } catch (error) {
      showErrorToast("Unable to delete plan");
    }
  };

  // --- UI Helpers ---
  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  const addFeatureField = () => {
    setFormData({ ...formData, features: [...formData.features, ""] });
  };

  const removeFeatureField = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: newFeatures });
  };

  const openEditModal = (plan) => {
    setEditingId(plan._id);
    setFormData({
      planType: plan.planType,
      title: plan.title,
      validityDays: plan.validityDays,
      price: plan.price,
      originalPrice: plan.originalPrice,
      discountPercent: plan.discountPercent,
      features: plan.features?.length > 0 ? plan.features : [""],
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormData({
      planType: "monthly",
      title: "",
      validityDays: 30,
      price: 0,
      originalPrice: 0,
      discountPercent: 0,
      features: [""],
    });
  };

  return (
    <div className="p-4">
      <ToastContainer />

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
            Subscription Plans
          </h1>
          <p className="text-sm text-slate-500">
            Manage pricing and features for your users
          </p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 bg-[#2563eb] text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 shadow-md transition-all active:scale-95 font-medium"
        >
          <FiPlus size={20} /> Create New Plan
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <FiLoader className="animate-spin text-blue-600" size={40} />
        </div>
      ) : plans.length === 0 ? (
        <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-20 flex flex-col items-center text-center">
          <FiInbox size={48} className="text-slate-300 mb-4" />
          <p className="text-slate-500">No subscription plans found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div
              key={plan._id}
              className={`bg-white rounded-2xl border-2 p-6 transition-all ${plan.isActive ? "border-slate-100 shadow-sm" : "border-slate-200 opacity-75 grayscale"}`}
            >
              <div className="flex justify-between items-start mb-4">
                <span
                  className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${plan.planType === "yearly_prime" ? "bg-purple-100 text-purple-600" : "bg-blue-100 text-blue-600"}`}
                >
                  {plan.planType.replace("_", " ")}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => openEditModal(plan)}
                    className="p-1.5 hover:bg-blue-50 text-slate-400 hover:text-blue-600 rounded-lg transition-all"
                  >
                    <FiEdit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(plan._id)}
                    className="p-1.5 hover:bg-red-50 text-slate-400 hover:text-red-600 rounded-lg transition-all"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </div>

              <h3 className="text-xl font-bold text-slate-800 mb-1">
                {plan.title}
              </h3>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-3xl font-black text-slate-900">
                  ₹{plan.price}
                </span>
                <span className="text-sm text-slate-400 line-through font-medium">
                  ₹{plan.originalPrice}
                </span>
                <span className="text-xs font-bold text-green-500 bg-green-50 px-2 py-0.5 rounded">
                  {plan.discountPercent}% OFF
                </span>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase">
                  <FiClock className="text-blue-500" /> {plan.validityDays} Days
                  Validity
                </div>
                <div className="pt-2 border-t border-slate-50">
                  {plan.features?.map((f, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 text-sm text-slate-600 mb-1.5"
                    >
                      <FiCheckCircle
                        className="text-green-500 flex-shrink-0"
                        size={14}
                      />{" "}
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-50">
                <span className="text-[10px] font-bold text-slate-400 uppercase">
                  Order: {plan.order}
                </span>
                <span
                  className={`text-[10px] font-bold px-2 py-1 rounded ${plan.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                >
                  {plan.isActive ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* --- ADD/EDIT MODAL --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50/50">
              <h2 className="text-xl font-bold text-slate-800">
                {editingId ? "Edit Subscription" : "New Plan"}
              </h2>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-600"
              >
                <FiX size={24} />
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 max-h-[75vh] overflow-y-auto custom-scrollbar"
            >
              {/* Left Column: Basic Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1">
                    Plan Title
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    className="w-full px-4 py-2 border rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. Monthly Pro"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1">
                    Plan Type
                  </label>
                  <select
                    value={formData.planType}
                    onChange={(e) =>
                      setFormData({ ...formData, planType: e.target.value })
                    }
                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                    <option value="yearly_prime">Yearly Prime</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">
                      Price (₹)
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.price}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          price: Number(e.target.value),
                        })
                      }
                      className="w-full px-4 py-2 border rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">
                      Original Price (₹)
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.originalPrice}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          originalPrice: Number(e.target.value),
                        })
                      }
                      className="w-full px-4 py-2 border rounded-xl"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">
                      Discount (%)
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.discountPercent}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          discountPercent: Number(e.target.value),
                        })
                      }
                      className="w-full px-4 py-2 border rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">
                      Validity (Days)
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.validityDays}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          validityDays: Number(e.target.value),
                        })
                      }
                      className="w-full px-4 py-2 border rounded-xl"
                    />
                  </div>
                </div>
              </div>

              {/* Right Column: Features List */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                <label className="block text-sm font-bold text-slate-700 mb-3">
                  Plan Features
                </label>
                <div className="space-y-3">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) =>
                          handleFeatureChange(index, e.target.value)
                        }
                        className="flex-1 px-3 py-2 border rounded-lg text-sm outline-none focus:border-blue-500"
                        placeholder="e.g. Access to all quizzes"
                      />
                      {formData.features.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeFeatureField(index)}
                          className="text-red-400 hover:text-red-600"
                        >
                          <FiX />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addFeatureField}
                    className="flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:underline pt-2"
                  >
                    <FiPlus /> ADD FEATURE
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="md:col-span-2 flex gap-3 mt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-3 border border-slate-200 text-slate-600 rounded-xl font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold shadow-lg shadow-blue-200 transition-all active:scale-95"
                >
                  {editingId ? "Update Plan" : "Create Plan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;
